import React from 'react';
import { Form } from 'react-final-form';
import { FORM_ERROR, SubmissionErrors } from 'final-form';
import { useSearchParams } from 'react-router-dom';
import { Box, Divider, Grid, Typography } from '@hbf/dsl';
import { useIntl } from 'ha/i18n';
import { ForgotPassword } from 'ha/components/LoginForm/components/ForgotPassword';
import { FormSwitch } from 'ha/components/LoginForm/components/FormSwitch';
import { useConfig } from 'ha/helpers/config';
import { GoogleAuthBtn } from 'ha/components/SocialButtons/GoogleAuthBtn/GoogleAuthBtn';
import { FacebookAuthBtn } from 'ha/components/SocialButtons/FacebookAuthBtn/FacebookAuthBtn';
import { ErrorMessages } from 'ha/constants/ErrorMessages';
import { validateForm } from '../helpers/validateForm';
import { LoginFields as Fields } from './Fields';
import { Submit } from './Submit';

interface Props {
  onLogin: (
    fields: { email: string; password: string },
    nextUrl: string,
  ) => void;
  isLoading: boolean;
  switchType?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  isAdvertiserRoute?: boolean;
  nextUrlProp?: string;
}

const LoginForm: React.FC<Props> = ({
  onLogin,
  isLoading,
  switchType = () => undefined,
  isAdvertiserRoute,
  nextUrlProp,
}) => {
  const { T, urlResolver } = useIntl();
  const { google, facebook } = useConfig();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('return_url');
  const nextUrl = nextUrlProp || urlResolver.getRedirectUrl(returnUrl);
  const fbAuthSearchParams = {
    ...facebook,
    state: nextUrl,
  };
  const googleAuthSearchParams = {
    ...google,
    state: nextUrl,
  };
  const onSubmit: (values: {
    email: string;
    password: string;
  }) =>
    | void
    | SubmissionErrors
    | Promise<SubmissionErrors | undefined>
    | undefined = React.useCallback(
    async values => {
      try {
        await onLogin(values, nextUrl);
      } catch (error) {
        if (error.errors.submitError) {
          return {
            [FORM_ERROR]: T(
              error.errors.submitError || ErrorMessages.something_went_wrong,
            ),
          };
        }

        return error.errors;
      }

      return null;
    },
    [nextUrlProp],
  );

  return (
    <Form onSubmit={onSubmit} validate={fields => validateForm(fields, T)}>
      {props => {
        return (
          <form onSubmit={props.handleSubmit} data-test-locator="Login form">
            <Grid
              container
              direction="column"
              justifyContent="center"
              spacing={2}
            >
              <Grid item data-test-locator="Login header">
                <Box pb={1}>
                  <Typography.Paragraph align="center">
                    {isAdvertiserRoute
                      ? T(
                          'Increase your rental income & save time with online bookings',
                        )
                      : T(
                          'Find your perfect place & contact advertisers directly',
                        )}
                  </Typography.Paragraph>
                </Box>
              </Grid>
              <Grid item container spacing={1} direction="column">
                <Grid item>
                  <FacebookAuthBtn
                    searchParams={fbAuthSearchParams}
                    trackName="Clicked Sign up with Facebook"
                  >
                    {T('Continue with Facebook')}
                  </FacebookAuthBtn>
                </Grid>
                <Grid item>
                  <GoogleAuthBtn searchParams={googleAuthSearchParams}>
                    {T('Continue with Google')}
                  </GoogleAuthBtn>
                </Grid>
              </Grid>
              <Divider kind="spaced-l" text={T('OR')} />
              {props.submitError && (
                <Grid item>
                  <Typography.Caption
                    color="error"
                    data-test-locator="Login submitError"
                  >
                    {props.submitError}
                  </Typography.Caption>
                </Grid>
              )}
              <Fields />
              <Grid item>
                <Box mt={1}>
                  <Submit isLoading={isLoading} />
                </Box>
              </Grid>
              <Grid item container alignItems="center" direction="column">
                <Grid item>
                  <Box mb={1}>
                    <ForgotPassword />
                  </Box>
                </Grid>
                <FormSwitch nextUrl={nextUrl} onSwitch={switchType} />
              </Grid>
            </Grid>
          </form>
        );
      }}
    </Form>
  );
};

export { LoginForm };
